/**
 * Change Password Page Component
 *
 * Full page view for forced password change flow.
 * Displays ChangePasswordForm and handles success flow (logout + redirect).
 * Used when user has passwordChangeRequired flag set to true.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Paper } from '@mui/material';
import { useDispatch } from 'react-redux';
import ChangePasswordForm from '../components/ChangePasswordForm';
import { logout } from '../slices/authSlice';

/**
 * ChangePasswordPage - Full page for password change
 *
 * Features:
 * - Centered layout with Paper card
 * - ChangePasswordForm component
 * - Auto-logout after successful password change
 * - Redirect to login with success message
 *
 * Flow:
 * 1. User fills form and submits
 * 2. Backend validates and updates password
 * 3. On success: dispatch logout action (clears tokens)
 * 4. Navigate to /login with success message in state
 * 5. User must login again with new password
 */
const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /**
   * Handle successful password change
   * Forces logout and redirects to login page
   */
  const handleSuccess = () => {
    // Force logout to invalidate old tokens
    dispatch(logout());

    // Redirect to login with success message
    navigate('/login', {
      state: {
        message: 'Hasło zostało zmienione pomyślnie. Zaloguj się ponownie używając nowego hasła.',
      },
    });
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        py: 4,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          p: 4,
        }}
      >
        <ChangePasswordForm onSuccess={handleSuccess} />
      </Paper>
    </Container>
  );
};

export default ChangePasswordPage;
